import type { Filter, FilterRelations, FunctionCall } from '@sisense/sdk-data';
import { translateQueryToJSON } from '@sisense/sdk-ui/analytics-composer';

function isFilterRelations(value: unknown): value is FilterRelations {
	return (
		value != null &&
		typeof value === 'object' &&
		('filters' in value || 'relations' in value)
	);
}

function extractFiltersFromRelations(
	filterRelations: FilterRelations,
): Filter[] {
	// FilterRelations may have a 'filters' property containing the actual filter array
	if (
		'filters' in filterRelations &&
		Array.isArray(filterRelations.filters)
	) {
		return filterRelations.filters;
	}
	// If the structure is different, return empty array rather than breaking
	return [];
}

/**
 * Compact filter list for LLM-facing chart/dashboard summaries.
 * Translates CSDK {@link Filter} instances via {@link translateQueryToJSON}.
 */
export function getFiltersJsonForLlmSummary(
	csdkFilters:
		| Filter[]
		| FilterRelations
		| readonly Filter[]
		| undefined
		| null,
): unknown[] {
	let raw: Filter[];
	if (Array.isArray(csdkFilters)) {
		raw = [...csdkFilters];
	} else if (isFilterRelations(csdkFilters)) {
		raw = extractFiltersFromRelations(csdkFilters);
	} else {
		raw = [];
	}

	if (raw.length === 0) {
		return [];
	}

	const translationResult = translateQueryToJSON({
		measures: [],
		dimensions: [],
		filters: raw,
	});

	if (
		translationResult.success &&
		translationResult.data?.filters != null &&
		Array.isArray(translationResult.data.filters)
	) {
		return [...translationResult.data.filters];
	}

	return [];
}

function isFunctionCallNode(node: unknown): node is FunctionCall {
	if (node === null || typeof node !== 'object') {
		return false;
	}
	const fn = (node as { function?: unknown }).function;
	return typeof fn === 'string';
}

/**
 * Same as {@link getFiltersJsonForLlmSummary}, narrowed to compose-tree
 * {@link FunctionCall} nodes for typed LLM payloads (unknown entries are dropped).
 */
export function getFunctionCallFiltersForLlmSummary(
	csdkFilters:
		| Filter[]
		| FilterRelations
		| readonly Filter[]
		| undefined
		| null,
): FunctionCall[] {
	return getFiltersJsonForLlmSummary(csdkFilters).filter(isFunctionCallNode);
}
